import { HfsActionSetting, HfsSettingsActionEnum } from 'fleet-data-model';
import type {
  RoboticMowerReferenceStationResponse,
  RoboticMowerSettingsResponse,
} from '@fleet-bffs/api-contract';
import { bffWebApi } from '~/api';
import type { SettingValuePair } from '~/domain/roboticmower-details/components/RoboticMowerSettingsCard/types';
import type { AppDispatch } from '~/store/Store';

export interface MowerSettingUpdateResponse {
  followupNotRequired: boolean;
  iprId: string;
  success: boolean;
  settingType: string;
}

interface MowerSettingUpdateRequest {
  machineIds: AssetId[];
  settings: SettingValuePair[];
}

export interface RoboticMowerPairingResponse {
  id: string;
  picture: string;
  modelName: string;
  machineName: string;
}

export interface RoboticMowerPairingRequest {
  pairingCode: string;
  machineName: string;
  activate: boolean | null;
  accessGroupIds: string[];
}

const roboticMowerAPI = bffWebApi.injectEndpoints({
  endpoints: (build) => ({
    approveFota: build.mutation<void, string>({
      query: (iprId) => ({
        url: `${process.env.ENDPOINT_BFF}/robotic-mowers/approve-fota/${iprId}`,
        method: 'POST',
      }),
    }),
    approveCsFota: build.mutation<void, string>({
      query: (iprId) => ({
        url: `${process.env.ENDPOINT_BFF}/robotic-mowers/cs/approve-fota/${iprId}`,
        method: 'POST',
      }),
    }),
    updateRoboticSettings: build.mutation<MowerSettingUpdateResponse[], MowerSettingUpdateRequest>({
      query: ({ machineIds, settings }) => ({
        url: `${process.env.ENDPOINT_BFF}/robotic-mowers-settings/`,
        method: 'POST',
        body: {
          machineIds,
          settings,
        },
      }),
    }),
    getRoboticSettings: build.query<RoboticMowerSettingsResponse, { assetId: string }>({
      query: ({ assetId }) => ({
        url: `${process.env.ENDPOINT_BFF}/robotic-mowers-settings/${assetId}`,
        method: 'GET',
      }),
    }),
    getReferenceStationDetails: build.query<RoboticMowerReferenceStationResponse, string>({
      query: (assetId) => ({
        url: `${process.env.ENDPOINT_BFF}/robotic-mowers/${assetId}/reference-station`,
        method: 'GET',
      }),
    }),
    roboticMowerPairing: build.mutation<RoboticMowerPairingResponse, RoboticMowerPairingRequest>({
      query: ({ pairingCode, machineName, activate, accessGroupIds }) => ({
        url: `/machines/robotic/pair/${pairingCode}`,
        method: 'POST',
        body: {
          machineName,
          activate,
          accessGroupIds,
        },
      }),
    }),
  }),
});

export const {
  useUpdateRoboticSettingsMutation,
  useGetReferenceStationDetailsQuery,
  useApproveFotaMutation,
  useApproveCsFotaMutation,
  useGetRoboticSettingsQuery,
  useRoboticMowerPairingMutation,
} = roboticMowerAPI;

export default roboticMowerAPI;

export const patchRoboticMowerHfsSettingValue =
  (payload: {
    settingDataOverwrites: HfsActionSetting;
    hfsActionId: HfsSettingsActionEnum;
    assetId: string;
  }) =>
  (dispatch: AppDispatch) => {
    const { settingDataOverwrites, hfsActionId, assetId } = payload;
    dispatch(
      roboticMowerAPI.util.updateQueryData('getRoboticSettings', { assetId }, (draft) => {
        const target = draft.hfsActions.find((item) => item.hfsAction === hfsActionId);
        if (target?.settingData !== undefined) {
          // @ts-ignore
          target.settingData = {
            ...target.settingData,
            ...settingDataOverwrites,
            // The mower accepted a value we sent, so it is no longer the invalid one it reported
            invalidValue: false,
          };
        }
      })
    );
  };
